import { useMemo, useState, useEffect } from "react";
import { apiGet } from "../../../api/http";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Cell, Legend,
} from "recharts";
import type { Activity } from "./ActivityManager";

interface Props {
  tenantLegacyId: string;
  activities: Activity[];
}

const TYPE_COLORS: Record<string, string> = {
  "观赛活动": "#ef4444", "线下聚会": "#f59e0b", "年度大会": "#14b8a6",
  "球迷嘉年华": "#22c55e", "训练营": "#0ea5e9", "志愿活动": "#ec4899",
  "赛事组织": "#f97316", "其他": "#9ca3af",
};

export default function ActivityDashboard({ tenantLegacyId, activities }: Props) {
  const [trendRows, setTrendRows] = useState<{ day: string; count: number }[]>([]);

  useEffect(() => {
    let cancelled = false;
    void apiGet<Array<{ date: string; count: number }>>(
      `/api/tenants/${tenantLegacyId}/stats/activity-enrollment-trend?days=12`,
    )
      .then((rows) => {
        if (cancelled) return;
        setTrendRows(
          rows.map((r) => {
            const d = new Date(`${r.date}T12:00:00`);
            return {
              day: `${d.getMonth() + 1}/${d.getDate()}`,
              count: r.count,
            };
          }),
        );
      })
      .catch(() => {
        if (!cancelled) setTrendRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  const stats = useMemo(() => {
    const total = activities.length;
    const totalEnrolled = activities.reduce((s, a) => s + a.enrolled, 0);
    const totalCapacity = activities.reduce((s, a) => s + a.capacity, 0);
    const ongoing = activities.filter((a) => a.status === "ongoing" || a.status === "full").length;
    const avgRate = totalCapacity > 0 ? Math.round((totalEnrolled / totalCapacity) * 100) : 0;
    const pending = activities.filter((a) => a.status === "pending").length;
    return { total, totalEnrolled, ongoing, avgRate, pending };
  }, [activities]);

  const enrollmentData = useMemo(
    () =>
      activities
        .filter((a) => a.status !== "draft" && a.capacity > 0)
        .slice(0, 8)
        .map((a) => ({
          name: a.title.length > 8 ? a.title.slice(0, 8) + "…" : a.title,
          enrolled: a.enrolled,
          capacity: a.capacity,
          rate: Math.round((a.enrolled / a.capacity) * 100),
          status: a.status,
        })),
    [activities]
  );

  const typeData = useMemo(() => {
    const counts: Record<string, number> = {};
    activities.forEach((a) => {
      if (a.activityType) counts[a.activityType] = (counts[a.activityType] ?? 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [activities]);

  const statCards = [
    { label: "活动总数",   value: stats.total,        icon: "ri-calendar-event-line", color: "bg-red-50 text-red-600",   sub: "全部状态" },
    { label: "进行中活动", value: stats.ongoing,       icon: "ri-loader-4-line",       color: "bg-green-50 text-green-600", sub: "报名中 / 已报满" },
    { label: "累计报名",   value: stats.totalEnrolled, icon: "ri-group-line",           color: "bg-amber-50 text-amber-600", sub: "人次" },
    { label: "平均上座率", value: `${stats.avgRate}%`, icon: "ri-percent-line",         color: "bg-teal-50 text-teal-600",  sub: "已发布活动" },
    { label: "待审核",     value: stats.pending,       icon: "ri-timer-line",           color: "bg-orange-50 text-orange-600", sub: "待处理" },
  ];

  const customTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm text-xs">
        <p className="font-semibold text-gray-800 mb-1.5">{label}</p>
        {payload.map((p: any) => (
          <p key={p.name} style={{ color: p.color }} className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color }}></span>
            {p.name}：{p.value}{p.name === "报名率" ? "%" : " 人"}
          </p>
        ))}
      </div>
    );
  };

  const trendTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm text-xs">
        <p className="font-semibold text-gray-800 mb-1">{label}</p>
        <p className="text-red-500">{payload[0].value} 次报名</p>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Stat Cards */}
      <div className="grid grid-cols-5 gap-3">
        {statCards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl mb-3 ${c.color}`}>
              <i className={`${c.icon} text-lg`}></i>
            </div>
            <p className="text-2xl font-bold text-gray-900">{c.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{c.label}</p>
            <p className="text-[10px] text-gray-300 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-5">
        {/* Enrollment Bar Chart */}
        <div className="col-span-2 bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-sm font-bold text-gray-800">各活动报名情况</h4>
              <p className="text-xs text-gray-400 mt-0.5">已发布活动的报名人数与容量对比</p>
            </div>
          </div>
          {enrollmentData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={enrollmentData} barGap={2} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <Tooltip content={customTooltip} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "11px", color: "#6b7280" }} />
                <Bar dataKey="capacity" name="容量" fill="#f3f4f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="enrolled" name="已报名" fill="#ef4444" radius={[4, 4, 0, 0]}>
                  {enrollmentData.map((_, i) => (
                    <Cell key={i} fill={_.rate >= 80 ? "#ef4444" : _.rate >= 50 ? "#f97316" : "#fca5a5"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-300">
              <div className="text-center">
                <i className="ri-bar-chart-2-line text-4xl block mb-2"></i>
                <p className="text-sm">暂无活动数据</p>
              </div>
            </div>
          )}
        </div>

        {/* Activity Type Distribution */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <h4 className="text-sm font-bold text-gray-800 mb-1">活动类型分布</h4>
          <p className="text-xs text-gray-400 mb-4">各类型活动占比</p>
          <div className="space-y-2.5">
            {typeData.map((item) => {
              const total = typeData.reduce((s, d) => s + d.value, 0);
              const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
              const color = TYPE_COLORS[item.name] ?? "#9ca3af";
              return (
                <div key={item.name}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }}></span>
                      <span className="text-gray-600">{item.name}</span>
                    </div>
                    <span className="text-gray-500 font-medium">{item.value} 个 · {pct}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }}></div>
                  </div>
                </div>
              );
            })}
            {typeData.length === 0 && <p className="text-xs text-gray-400 text-center py-4">暂无数据</p>}
          </div>
        </div>
      </div>

      {/* Registration Trend */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-bold text-gray-800">近期报名趋势</h4>
            <p className="text-xs text-gray-400 mt-0.5">最近 12 天各渠道每日新增报名人次</p>
          </div>
          <span className="text-xs bg-red-50 text-red-600 px-3 py-1 rounded-full">近 12 天</span>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={trendRows.length > 0 ? trendRows : [{ day: "—", count: 0 }]} margin={{ top: 5, right: 0, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
            <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
            <Tooltip content={trendTooltip} />
            <Area type="monotone" dataKey="count" name="报名人次" stroke="#ef4444" strokeWidth={2} fill="url(#trendGrad)" dot={false} activeDot={{ r: 4, fill: "#ef4444" }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Member participation leaderboard */}
      <div className="grid grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <h4 className="text-sm font-bold text-gray-800 mb-4">上座率排行</h4>
          <div className="space-y-3">
            {[...activities]
              .filter((a) => a.status !== "draft" && a.capacity > 0)
              .sort((a, b) => (b.enrolled / b.capacity) - (a.enrolled / a.capacity))
              .slice(0, 5)
              .map((a, i) => {
                const rate = Math.round((a.enrolled / a.capacity) * 100);
                const rankColors = ["text-amber-500", "text-gray-400", "text-orange-400", "text-gray-300", "text-gray-300"];
                return (
                  <div key={a.id} className="flex items-center gap-3">
                    <span className={`text-sm font-bold w-5 text-center ${rankColors[i]}`}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-700 truncate">{a.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-red-400" style={{ width: `${rate}%` }}></div>
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0 w-8 text-right">{rate}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            {activities.filter((a) => a.status !== "draft").length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">暂无数据</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <h4 className="text-sm font-bold text-gray-800 mb-4">活动状态概览</h4>
          {[
            { label: "报名中", count: activities.filter((a) => a.status === "ongoing").length,  color: "bg-green-100 text-green-700",  icon: "ri-loader-4-line" },
            { label: "已报满", count: activities.filter((a) => a.status === "full").length,     color: "bg-red-100 text-red-700",     icon: "ri-group-2-line" },
            { label: "待审核", count: activities.filter((a) => a.status === "pending").length,  color: "bg-amber-100 text-amber-700", icon: "ri-timer-line" },
            { label: "草稿",   count: activities.filter((a) => a.status === "draft").length,    color: "bg-gray-100 text-gray-600",   icon: "ri-draft-line" },
            { label: "已结束", count: activities.filter((a) => a.status === "ended").length,    color: "bg-gray-100 text-gray-400",   icon: "ri-flag-line" },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
              <div className="flex items-center gap-2.5">
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs ${item.color}`}>
                  <i className={item.icon}></i>
                </div>
                <span className="text-sm text-gray-600">{item.label}</span>
              </div>
              <span className="text-sm font-bold text-gray-800">{item.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
